import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, Compass, Flame, HelpCircle, ListChecks, PencilRuler, RefreshCw, Sparkles, Target,
} from "lucide-react";
import { api, getStudentId } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ConceptPicker from "@/components/ConceptPicker";
import VoicePromptBanner from "@/components/VoicePromptBanner";
import StudyTogetherCard from "@/components/StudyTogetherCard";
import { useVoicePrompt } from "@/hooks/use-voice-prompt";

const ACTIONS = [
  { key: "learn", label: "Learn", sub: "Teach me something", icon: BookOpen, color: "from-primary/20 to-primary/5", accent: "text-primary", needsPicker: true, route: (id) => `/tutor/learn/${id}` },
  { key: "practice", label: "Practice", sub: "Let me practice", icon: PencilRuler, color: "from-success/20 to-success/5", accent: "text-success", needsPicker: true, route: (id) => `/practice/${id}` },
  { key: "assess", label: "Assess", sub: "Test my knowledge", icon: ListChecks, color: "from-destructive/15 to-destructive/5", accent: "text-destructive", needsPicker: true, route: (id) => `/assessment/${id}` },
  { key: "homework", label: "Homework Help", sub: "Help me understand", icon: HelpCircle, color: "from-secondary/25 to-secondary/5", accent: "text-secondary", needsPicker: false, route: () => `/tutor/homework` },
  { key: "revise", label: "Revise", sub: "What I need to revisit", icon: RefreshCw, color: "from-accent/20 to-accent/5", accent: "text-accent", needsPicker: false, route: () => `/mastery` },
  { key: "explore", label: "Explore", sub: "Learn something new", icon: Compass, color: "from-primary/15 to-secondary/10", accent: "text-primary", needsPicker: false, route: () => `/tutor/explore` },
];

export default function Home() {
  const navigate = useNavigate();
  const studentId = getStudentId();
  const [student, setStudent] = useState(null);
  const [masteryList, setMasteryList] = useState([]);
  const [revision, setRevision] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null);

  useEffect(() => {
    api.get(`/students/${studentId}`).then(({ data }) => setStudent(data));
    api.get(`/mastery/${studentId}`).then(({ data }) => setMasteryList(data));
    api.get(`/mastery/${studentId}/revision`).then(({ data }) => setRevision(data));
  }, [studentId]);

  const handleActionClick = (action) => {
    if (action.needsPicker) {
      setActiveAction(action);
      setPickerOpen(true);
    } else {
      navigate(action.route());
    }
  };

  const handleConceptSelect = (conceptId) => {
    setPickerOpen(false);
    if (activeAction) navigate(activeAction.route(conceptId));
  };

  const xpForLevel = (student?.level || 1) * 150;
  const xpProgress = student ? Math.min(100, ((student.xp % 150) / 150) * 100) : 0;

  const { blocked: voiceBlocked, retry: retryVoice } = useVoicePrompt(
    student ? `Hi ${student.name}, please select the appropriate option.` : "",
    student ? `home-greeting-${studentId}` : null,
    { enabled: !!student, once: true }
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar student={student} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <VoicePromptBanner blocked={voiceBlocked} onRetry={retryVoice} testId="home-voice-unlock-banner" />
        <div className="bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-6 sm:p-8 text-primary-foreground mb-8 relative overflow-hidden animate-float-in" data-testid="student-greeting-card">
          <Sparkles className="absolute right-6 top-6 opacity-30" size={64} />
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold mb-1" data-testid="home-welcome-heading">
            Welcome back{student?.name ? `, ${student.name}` : ""}! 👋
          </h1>
          <p className="text-primary-foreground/80 text-sm mb-5">Class {student?.standard} · {student?.board}</p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1.5 rounded-full text-sm font-semibold">
              <Flame size={16} /> {student?.streak_count || 0} day streak
            </div>
            <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1.5 rounded-full text-sm font-semibold">
              <Target size={16} /> Level {student?.level || 1}
            </div>
            <div className="flex-1 min-w-[140px] max-w-xs">
              <div className="flex justify-between text-xs mb-1 text-primary-foreground/80">
                <span>XP</span><span>{student?.xp || 0} / {xpForLevel}</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-secondary rounded-full transition-all" style={{ width: `${xpProgress}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-10">
          <StudyTogetherCard />
        </div>

        <h2 className="text-lg font-heading font-bold mb-4">What would you like to do?</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 mb-10">
          {ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.key}
                data-testid={`action-tile-${action.key}`}
                onClick={() => handleActionClick(action)}
                className={`bg-gradient-to-br ${action.color} border border-border rounded-2xl p-5 sm:p-6 text-left hover:scale-[1.02] hover:shadow-lg transition-all duration-200 group`}
              >
                <Icon className={`${action.accent} mb-3`} size={28} />
                <p className="font-heading font-bold text-base">{action.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{action.sub}</p>
              </button>
            );
          })}
        </div>

        {revision.length > 0 && (
          <div className="mb-10" data-testid="home-revision-section">
            <h2 className="text-lg font-heading font-bold mb-4">Today's Recommended Revision</h2>
            <div className="flex flex-wrap gap-3">
              {revision.map((r) => (
                <button
                  key={r.concept_id}
                  data-testid={`revision-item-${r.concept_id}`}
                  onClick={() => navigate(`/tutor/revise/${r.concept_id}`)}
                  className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3 hover:border-accent transition-colors"
                >
                  <RefreshCw size={16} className="text-accent" />
                  <span className="text-sm font-semibold">{r.concept_name}</span>
                  <span className="text-xs text-muted-foreground">{r.recommended_minutes} min</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {masteryList.length > 0 && (
          <div data-testid="home-recent-topics">
            <h2 className="text-lg font-heading font-bold mb-4">Your Topics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {masteryList.slice(0, 8).map((m) => (
                <div key={m.concept_id} className="bg-card border border-border rounded-xl p-4" data-testid={`topic-card-${m.concept_id}`}>
                  <p className="text-sm font-semibold truncate">{m.concept_name}</p>
                  <p className="text-xs text-muted-foreground mb-2">{m.chapter_name}</p>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${m.bracket === "mastered" ? "bg-success" : m.bracket === "practicing" ? "bg-primary" : "bg-secondary"}`}
                      style={{ width: `${m.mastery_score}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{m.mastery_score}% mastery</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {activeAction && (
        <ConceptPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          board={student?.board}
          standard={student?.standard}
          onSelect={handleConceptSelect}
        />
      )}
    </div>
  );
}
